<template>
  <el-form
    v-if="model"
    class="form form--partial"
    ref="form"
    :model="model"
    :rules="rules"
    :label-position="labelPosition"
    :label-width="labelWidthForm"
    :validate-on-rule-change="false"
    @validate="onFieldValidation"
  >
    <el-row :gutter="gutter">
      <el-col
        v-for="(field, i) in addressFields"
        :key="`${key}-${field.key}`"
        :lg="fieldLg(field)"
        :md="fieldMd(field)"
        :sm="fieldMd(field)"
        :xs="24"
      >
        <el-form-item
          :label="fieldLabel(field)"
          :prop="fields[field.name].name"
          :required="fieldRequired(field)"
          :error="getFieldError(fields[field.name].name)"
        >
          <el-select
            v-if="field.name === 'country' || (field.name === 'state' && selectedCountryStateProvinces.length)"
            v-model="model[fields[field.name].name]"
            filterable
            ref="fields"
            value-key="id"
            :name="fields[field.name].name"
            :placeholder="fieldPlaceholder(field)"
            :disabled="field.name === 'country' && disableCountry"
            @change="field.name === 'country' ? onCountryChange() : null"
            @keydown.enter.native="() => handleSelectEnter(i)"
          >
            <i
              slot="prefix"
              class="icon-location"
            />
            <el-option
              v-for="option in fieldOptions(field)"
              :key="option.id"
              :label="option.label"
              :value="option.id"
            />
          </el-select>
          <el-input
            v-else
            v-model="model[fields[field.name].name]"
            ref="fields"
            :name="fields[field.name].name"
            :placeholder="fieldPlaceholder(field)"
            prefix-icon="icon-location"
          />
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import { isEqual } from 'lodash';

import { i18n, i18nExists } from '@shared/i18n';

import { FormSchema } from '@shared/form/form-schema';
import { AddressModel } from '@shared/model/address-model';

import countries from '@enums/country.json';
import stateProvinces from '@enums/stateProvinces.json';
import addressFieldsByCountry from '@enums/addressFieldsByCountry.json';

const { fields } = AddressModel;
const formSchema = new FormSchema([
  fields.city,
  fields.country,
  fields.line1,
  fields.line2,
  fields.postalCode,
  fields.state,
]);

export default {
  name: 'app-address-form-partial',

  data() {
    return {
      key: `app-address-form-partial--${+new Date}`,
      model: null,
      initialModel: null,
      i18n
    };
  },

  props: {
    fieldName: {
      type: String,
      default: 'address',
    },
    address: Object,
    errors: {
      type: Object,
      default: () => ({}),
    },
    labelPosition: String,
    labelWidthForm: String,
    gutter: {
      type: Number,
      default: 16,
    },
    disableCountry: Boolean,
  },

  computed: {
    fields() {
      return fields;
    },

    rules() {
      const rules = formSchema.rules();
      const nonRequiredFields = this.addressFields.filter(({ required }) => !required);
      for (const field of nonRequiredFields) delete rules[field.name];
      return rules;
    },

    addressFields() {
      const { country } = this.model;
      if (!country) return addressFieldsByCountry['initial'];
      return country in addressFieldsByCountry
        ? addressFieldsByCountry[country]
        : addressFieldsByCountry['default'];
    },

    hasStateProvinceField() {
      return this.model.country !== 'GB';
    },

    countries() {
      return this.fields.country.options.filter(({ id }) => {
        return countries.some(countryId => countryId === id);
      });
    },

    selectedCountryStateProvinces() {
      const selectedCountry = this.model[fields.country.name]
      if (!selectedCountry) return []

      if (!(selectedCountry in stateProvinces)) return []

      return stateProvinces[selectedCountry].map(code => {
        return {
          id: code,
          label: i18n(`enumerators.stateProvince.${selectedCountry}.${code}`)
        }
      })
    },
  },

  mounted() {
    this.model = formSchema.initialValues(this.address);
    this.initialModel = { ...this.model };
  },
  
  methods: {
    fieldLg(field) {
      if (field.name === 'line2' || field.name === 'postalCode') {
        if (this.addressFields.length === 4) return 8;
        return 12;
      }

      if (field.name === 'country' || field.name === 'state' || field.name === 'city') {
        if (this.addressFields.length === 5) return 12;
        return 8;
      }

      return 24;
    },

    fieldMd(field) {
      if (field.name === 'country' || field.name === 'state' || field.name === 'postalCode') {
        return 12;
      }

      if (field.name === 'city') {
        if (this.addressFields.length === 5) return 24;
        return 12;
      }

      return 24;
    },

    fieldLabel(field) {
      if (field.label && i18nExists(`entities.address.fields.${field.label}`)) return i18n(`entities.address.fields.${field.label}`);
      return this.fields[field.name].label;
    },

    fieldOptions(field) {
      let options = []

      if (field.name === 'country') options = this.countries
      if (field.name === 'state') options = this.selectedCountryStateProvinces

      return options.sort((a,b) => {
        var valA = a.label.toUpperCase();
        var valB = b.label.toUpperCase();
        return (valA < valB) ? -1 : (valA > valB) ? 1 : 0;
      })
    },

    fieldPlaceholder(field) {
      if (field.label && i18nExists(`entities.address.placeholders.${field.label}`)) return i18n(`entities.address.placeholders.${field.label}`);
      return i18n(`entities.address.placeholders.${fields[field.name].name}`)
    },

    fieldRequired(field) {
      return field.required
    },

    validate() {
      return this.$refs.form.validate();
    },

    getFieldError(fieldName) {
      const mappedError = this.errors[fieldName];
      if (!mappedError) return null;
      return mappedError.message;
    },

    onFieldValidation(formFieldName) {
      const fieldName = this.fieldName ? `${this.fieldName}.${formFieldName}` : formFieldName;
      this.$emit('validate', fieldName);
    },

    onCountryChange() {
      this.model.state = undefined;
      this.$refs.form.clearValidate();
    },

    doReset() {
      this.model = { ...this.initialModel };
    },

    handleSelectEnter(i) {
      const $field = this.$refs.fields[i];
      const optionEl = $field.options && $field.options.filter(o => o.visible)[0]
      if (optionEl) optionEl.selectOptionClick()
      $field.blur()
    },
  },

  watch: {
    model: {
      deep: true,
      handler(val) {
        this.$emit('update:address', val);
      },
    },
    address: {
      deep: true,
      handler(val, oldVal) {
        if (isEqual(val, oldVal)) return;
        this.model = formSchema.initialValues(this.address);
      },
    },
  },
};
</script>

<style scoped>
.layout .form--partial {
  margin-bottom: 22px;
  padding: 0;
}
</style>
